import { compact, isEmpty, cloneDeep, pick } from "lodash";
import moment from "moment";
import { ConsultantsQueryInput } from "@bonliva-crm/graphql";
import { removeTimezoneOffset } from "@bonliva-crm/common";
import { ConsultantFilterState } from "./consultant-filter-state";
import { DrivingLicenceType } from "@bonliva-crm/shared";

/**
 * The fields `ConsultantsQueryProfessionsFormInput` declares. Anything else in
 * restored form state is dropped before the request leaves the browser — see the
 * whitelist in `mapFiltersToConsultantQueryInput`. Keep in sync with the input
 * type in the GraphQL schema.
 */
export const PROFESSIONS_FORM_FIELDS: string[] = [
  "professions",
  "doctor",
  "expOnly",
  "psykolog",
  "radiographer",
  "socialWorkerForm",
  "sskForm",
  "teacherForm"
];

export function mapFiltersToConsultantQueryInput(
  data: Partial<ConsultantFilterState>,
  currentUserId: string
): ConsultantsQueryInput {
  const result: ConsultantsQueryInput = {};

  if (!isEmpty(data.text)) {
    result.text = data.text;
  }

  if (data.ageRange?.from && data.ageRange?.to) {
    result.birthdate = ageToDates(data.ageRange.from, data.ageRange.to);
  }

  if (!isEmpty(data.alphabetFilter)) {
    result.alphabetFilter = compact(data.alphabetFilter);
  }

  if (!isEmpty(data.initialsLastName)) {
    result.initialsLastName = compact(data.initialsLastName);
  }

  if (data.availability?.from && data.availability?.to) {
    result.availabilityDateRange = data.availability;
  }

  if (!isEmpty(data.availableForWorkInCountries)) {
    result.availableForWorkInCountries = data.availableForWorkInCountries;
  }

  if (data.bookingStatus) {
    result.bookingStatus = data.bookingStatus.bookingStatus;
    result.bookingStatusDate = removeTimezoneOffset(data.bookingStatus.bookingStatusDate);
    result.bookingStatusDateEnd = removeTimezoneOffset(
      data.bookingStatus.bookingStatusDateEnd
    );
  }

  if (data.bookingStatusOption) {
    result.bookingStatusOption = data.bookingStatusOption;
  }

  if (!isEmpty(data.callListIds)) {
    result.callListIds = data.callListIds.map((x) => x.value);
  }

  if (!isEmpty(data.competitors)) {
    result.competitors = data.competitors;
  }

  if (!isEmpty(data.customerIds)) {
    result.customerIds = data.customerIds.map((x) => x.value);
  }

  if (data.fitness) {
    result.fitness = data.fitness;
  }

  if (data.gdprIntegrationPolicy) {
    result.gdprIntegrationPolicy = [data.gdprIntegrationPolicy];
  }

  if (data.gdprMarketing) {
    result.gdprMarketing = [data.gdprMarketing];
  }

  if (data.gdprJobMailingEmail) {
    result.gdprJobMailingEmail = [data.gdprJobMailingEmail];
  }

  if (data.gdprJobMailingSms) {
    result.gdprJobMailingSms = [data.gdprJobMailingSms];
  }

  if (data.hasDrivingLicence === "auto") {
    result.hasDrivingLicence = DrivingLicenceType.Automatic;
  }

  if (data.hasDrivingLicence === "all") {
    result.hasDrivingLicence = DrivingLicenceType.Manual;
  }

  if (data.hasEmail) {
    result.hasEmail = data.hasEmail;
  }

  if (data.hasLegalPersonType) {
    result.hasLegalPersonType = data.hasLegalPersonType;
  }

  if (data.hasPhone) {
    result.hasPhone = data.hasPhone;
  }

  if (data.hprNumber) {
    result.hprNumber = parseStringBool(data.hprNumber);
  }

  if (!isEmpty(data.initialContact)) {
    result.initialContact = data.initialContact;
  }

  if (!isEmpty(data.knownSystems)) {
    result.systems = data.knownSystems;
  }

  if (data.lastContact?.dates?.from) {
    const { to: _to, from: _from } = data.lastContact.dates;

    const to = removeTimezoneOffset(_to);
    const from = removeTimezoneOffset(_from);

    if (from || to) {
      result.lastContactDate = { from, to };
    }

    if (data.lastContact.myActivity) {
      result.lastContactUserId = [currentUserId];
    }

    if (data.lastContact.type) {
      result.lastContactType = data.lastContact.type;
    }

    if (data.lastContact.selectType) {
      result.lastContactSelectType = data.lastContact.selectType;
    }
  }

  if (data.leadStatus) {
    result.leadStatus = data.leadStatus;
  }

  if (data.legalBasis) {
    result.legalBasis = data.legalBasis;
  }

  if (data.location) {
    result.location = data.location;
  }

  if (data.nationalQualification) {
    result.nationalQualification = data.nationalQualification;
  }

  if (!isEmpty(data.profession)) {
    const { interested, experienced, experience, ...rest } = cloneDeep(data.profession);

    // Whitelist against the fields `ConsultantsQueryProfessionsFormInput` actually
    // declares. Restored state comes from saved search profiles and localStorage
    // written by older clients, so it can still carry keys that have since been
    // deleted from the schema (`doctorNO`, removed by CRMDEV-4687) — replaying one
    // makes the server reject the entire query with BAD_USER_INPUT and the search
    // page fails until the user clears the profile (CRMDEV-7167).
    // `any` to keep the shape `data.profession` already has — `pick` would
    // otherwise widen it to `Pick<any, string>` and drop the required fields.
    const professions: any = pick(rest, PROFESSIONS_FORM_FIELDS);

    result.professionsForm = professions;
    if (professions?.psykolog?.experience?.value?.length > 0) {
      //fixme after remove legacy code
      const experience = professions.psykolog.experience.value.map((x) => {
        const v = x.split("|||");
        return `Psykolog|||${v[1]}`;
      });
      result.professionsForm.psykolog.experience.value = [experience.join("; ")];
    }

    result.experienced = experienced || "null";
    result.interested = interested || "null";
  }

  if (data.qualification) {
    result.qualification = data.qualification;
  }

  if (data.regions) {
    result.availabilityRegions = data.regions;
  }

  if (!isEmpty(data.responsiblePerson)) {
    result.responsiblePersonId = data.responsiblePerson;
  }

  if (data.wagesRange) {
    result.wageRange = data.wagesRange;
  }

  if (data.workedInNorway) {
    result.workedInNorway = data.workedInNorway;
  }

  if (data.workshifts) {
    result.availabilityWorkshifts = data.workshifts;
  }

  if (data.fullTextSearch) {
    result.fullTextSearch = data.fullTextSearch;
  }

  if (
    data.qualityCheck &&
    data.qualityCheck.qualityCheckName &&
    data.qualityCheck.qualityCheckStatuses?.length > 0
  ) {
    result.qualityCheckNames = [data.qualityCheck.qualityCheckName];
    result.qualityCheckStatuses = data.qualityCheck.qualityCheckStatuses;

    if (
      (data.qualityCheck?.from || data.qualityCheck?.to) &&
      !(
        data.qualityCheck.qualityCheckName === "Reference" ||
        data.qualityCheck.qualityCheckName === "ReferenceCheck"
      )
    ) {
      result.qualityCheckStatusesDate = {
        from: removeTimezoneOffset(data.qualityCheck.from) || null,
        to: removeTimezoneOffset(data.qualityCheck.to) || null
      };
    }
  }

  if (!isEmpty(data.tags)) {
    result.tags = data.tags.map((x) => x.value);
  }

  if (data.nextContact?.from || data.nextContact?.to) {
    result.nextContactDate = {
      from: data.nextContact.from || null,
      to: data.nextContact.to || null
    };
  }

  if (!isEmpty(data.pipelineStatus)) {
    result.pipelineStatus = data.pipelineStatus;
  }

  result.status = data.status;

  if (data.onlyFavourites) {
    result.onlyFavourites = true;
  }

  return result;
}

function ageToDates(from: number, to: number) {
  // basedate: someone who becomes X years old today might have been born X year + 364 days ago!
  const baseDate = moment.utc().startOf("day").subtract(1, "year").add(1, "day");

  return {
    from: baseDate.clone().subtract(to, "years").toDate(),
    to: moment.utc().startOf("day").subtract(from, "years").toDate()
  };
}

function parseStringBool(str: string) {
  return str === "true" ? true : str === "false" ? false : null;
}
